import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template154: Louvre (Museo del Louvre, Jardines Tullerías, Plaza Concordia). Todo desde backend.
// page.texts[0]=header, [1]=intro, [2]=título portada | page.sections[0..2] (title, body, imageId, sabiasQue[0]) | page.llegar | page.images (portada, tulleries), page.canvasImages (logo, louvre-sticker-cover, louvre-sticker)
const Template154 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const portadaTitle = page?.texts?.[2]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const portadaImage = getImageSrc("portada");
	const louvreSticker = getCanvasImageUrl("louvre-sticker-cover");
	const louvreSticker2 = getCanvasImageUrl("louvre-sticker");

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;

	const tulleriesImageSrc = getImageSrc(section1?.imageId);

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				height: "100vh",
				display: "flex",
				flexDirection: "column",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					backgroundColor: backgroundColor || "#f7f7f7",
					position: "relative",
				}}
			>
				{headerText && (
					<Box
						data-block-id="header"
						data-template-header
						sx={{
							backgroundColor: actualHeaderColor,
							color: headerTextColor || "#fff",
							textAlign: "center",
							padding: "15px 0",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "14px",
							fontWeight: 600,
							flexShrink: 0,
							position: "sticky",
							top: 0,
							zIndex: 99999,
						}}
					>
						{headerText}
						{!isSmallScreen && logoImage && (
							<img
								src={logoImage}
								alt=""
								style={{
									width: 80,
									position: "absolute",
									right: 2,
									top: 18,
									zIndex: 999999,
								}}
							/>
						)}
					</Box>
				)}

				{/* PORTADA */}
				{portadaImage && (
					<Box data-block-id="image-0" sx={{ position: "relative" }}>
						<img
							src={portadaImage}
							alt=""
							style={{ width: "100%", height: 200, objectFit: "cover" }}
						/>
						{portadaTitle && (
							<Box
								component="h1"
								sx={{
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									fontSize: isSmallScreen ? 26 : 32,
									textAlign: "center",
									position: "absolute",
									bottom: "0",
									margin: "10px",
									left: "20px",
									color: "#fff",
									textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
								}}
							>
								{portadaTitle}
							</Box>
						)}
						{louvreSticker && (
							<img
								src={louvreSticker}
								alt=""
								style={{
									width: isSmallScreen ? 80 : 100,
									position: "absolute",
									bottom: 50,
									left: isSmallScreen ? 15 : 18,
								}}
							/>
						)}
					</Box>
				)}

				<Box
					data-block-id="text-1"
					sx={{
						padding: "10px 12px",
						paddingBottom: "0px",
						position: "relative",
						marginTop: "10px",
					}}
				>
					{introText && (
						<Box
							component="p"
							sx={{ ...estiloP, marginTop: "-10px", marginBottom: "12px" }}
						>
							{introText}
						</Box>
					)}

					{/* Museo del Louvre */}
					{section0 && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
								{section0.sabiasQue?.[0] && (
									<Box sx={{ position: "absolute", top: -12, left: "42%" }}>
										<SabiasQue
											icono={section0.sabiasQue[0].icono}
											texto={section0.sabiasQue[0].texto}
										/>
									</Box>
								)}
							</Box>
						</Box>
					)}

					{/* Jardines de las Tullerías */}
					{section1 && (
						<Box
							data-block-id="text-3"
							sx={{
								backgroundColor: "#E8F4F8",
								padding: "8px",
								borderRadius: "12px",
								display: "flex",
								gap: "16px",
								alignItems: "center",
								position: "relative",
								marginTop: "16px",
							}}
						>
							{tulleriesImageSrc && (
								<img
									src={tulleriesImageSrc}
									alt=""
									style={{
										width: "35%",
										borderRadius: 12,
										objectFit: "cover",
										height: 90,
									}}
								/>
							)}
							<Box sx={{ width: tulleriesImageSrc ? "65%" : "100%" }}>
								{section1.title && (
									<Box component="h3" sx={estiloH3}>
										{section1.title}
									</Box>
								)}
								{section1.body && (
									<Box component="p" sx={estiloP}>
										{section1.body}
									</Box>
								)}
							</Box>
						</Box>
					)}

					{/* Plaza de la Concordia y Palais Royal */}
					{section2 && (
						<Box
							data-block-id="text-4"
							sx={{
								display: "flex",
								gap: "16px",
								marginTop: "16px",
								position: "relative",
							}}
						>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#FFF5E6",
										borderRadius: "12px",
									}}
								>
									{section2.title && (
										<Box component="h3" sx={estiloH3}>
											{section2.title}
										</Box>
									)}
									{section2.body}
								</Box>
							</Box>
							{section2.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -20, right: "30%" }}>
									<SabiasQue
										icono={section2.sabiasQue[0].icono}
										texto={section2.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Cómo llegar */}
					{llegar?.title && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#E8F4F8",
								border: `3px solid ${actualHeaderColor}`,
								borderRadius: "12px",
								padding: "12px 12px",
								fontSize: 12,
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#333",
								position: "relative",
								width: isSmallScreen ? "100%" : "100%",
								marginTop: "14px",
							}}
						>
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									color: actualHeaderColor,
									textTransform: "uppercase",
								}}
							>
								{llegar.title}
							</Box>
							{llegar.content && (
								<Box
									component="div"
									dangerouslySetInnerHTML={{ __html: llegar.content }}
									sx={{
										...estiloP,
										width: "100%",
										"& ul": { paddingLeft: "20px", margin: 0 },
									}}
								/>
							)}
							{louvreSticker2 && (
								<img
									src={louvreSticker2}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: isSmallScreen ? 0 : 20,
										right: isSmallScreen ? -150 : -34,
									}}
								/>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template154.thumbnail = "/images/template-1.png";

export default Template154;
